import BigNumber from 'bignumber.js'
import poolsConfig from 'config/constants/pools'
import sousChefABI from 'config/abi/sousChef.json'
import erc20ABI from 'config/abi/erc20.json'
import multicall from 'utils/multicall'
import { getMasterchefContract } from 'utils/contractHelpers'
import { getAddress } from 'utils/addressHelpers'
import { simpleRpcProvider } from 'utils/providers'
import { PoolCategory } from 'config/constants/types'

const nonBnbPools = poolsConfig.filter(
  (pool) => pool.stakingToken.symbol !== 'MONEY' && pool.poolCategory !== PoolCategory.AUTO
)

const bnbPools = poolsConfig.filter(
  (pool) => pool.stakingToken.symbol === 'MONEY' && pool.poolCategory !== PoolCategory.AUTO
)

const nonMasterPools = poolsConfig.filter(
  (pool) => pool.sousId !== 0 && pool.poolCategory !== PoolCategory.AUTO
)

const masterChefContract = getMasterchefContract()

export const fetchPoolsAllowance = async (account) => {
  const calls = nonBnbPools.map((pool) => ({
    address: pool.stakingToken.address,
    name: 'allowance',
    params: [account, getAddress(pool.contractAddress)],
  }))

  const allowances = await multicall(erc20ABI, calls)
  return nonBnbPools.reduce((acc, pool, index) => {
    const val = new BigNumber(allowances[index] || '0')
    return {
      ...acc,
      [pool.sousId]: BigNumber.maximum(val, 0).toJSON(),
    }
  }, {})
}

export const fetchUserBalances = async (account) => {
  const calls = nonBnbPools.map((pool) => ({
    address: pool.stakingToken.address,
    name: 'balanceOf',
    params: [account],
  }))
  const tokenBalancesRaw = await multicall(erc20ABI, calls)
  const tokenBalances = nonBnbPools.reduce((acc, pool, index) => {
    const val = new BigNumber(tokenBalancesRaw[index] || '0')
    return {
      ...acc,
      [pool.sousId]: BigNumber.maximum(val, 0).toJSON(),
    }
  }, {})

  const bnbBalance = await simpleRpcProvider.getBalance(account)
  const bnbBalances = bnbPools.reduce((acc, pool) => {
    const val = new BigNumber(bnbBalance.toString())
    return {
      ...acc,
      [pool.sousId]: BigNumber.maximum(val, 0).toJSON(),
    }
  }, {})

  return { ...tokenBalances, ...bnbBalances }
}

export const fetchUserStakeBalances = async (account) => {
  const calls = nonMasterPools.map((p) => ({
    address: getAddress(p.contractAddress),
    name: 'userInfo',
    params: [account],
  }))
  const userInfo = await multicall(sousChefABI, calls)

  const stakedBalances = nonMasterPools.reduce((acc, pool, index) => {
    const raw = userInfo[index]?.amount?._hex ?? '0x0'
    const val = new BigNumber(raw)
    return {
      ...acc,
      [pool.sousId]: BigNumber.maximum(val, 0).toJSON(),
    }
  }, {})

  const { amount: masterPoolAmount } = await masterChefContract.userInfo('0', account)
  const val = new BigNumber(masterPoolAmount.toString())

  return {
    ...stakedBalances,
    0: BigNumber.maximum(val, 0).toJSON(),
  }
}

export const fetchUserPendingRewards = async (account) => {
  const calls = nonMasterPools.map((p) => ({
    address: getAddress(p.contractAddress),
    name: 'pendingReward',
    params: [account],
  }))

  const res = await multicall(sousChefABI, calls)
  const pendingRewards = nonMasterPools.reduce((acc, pool, index) => {
    const val = new BigNumber(res[index] || '0')
    return {
      ...acc,
      [pool.sousId]: BigNumber.maximum(val, 0).toJSON(),
    }
  }, {})

  const pendingReward = await masterChefContract.pendingMoney('0', account)
  const val = new BigNumber(pendingReward.toString())

  return {
    ...pendingRewards,
    0: BigNumber.maximum(val, 0).toJSON(),
  }
}
